import {
  MarketStatus,
  OrderSide,
  OrderType,
} from '@katanaperps/katana-perps-sdk';
import React, { useReducer, useEffect, useState } from 'react';
import styled from 'styled-components';

import { KatanaApiError } from 'api/apiPatchConstants';
import { calculateBuyingPower } from 'api/apiUtils';
import { useMarketQuery } from 'api/queries/marketsQueries';
import {
  useOrdersQueriesInvalidator,
  useOrdersQuery,
} from 'api/queries/orderQueries';
import { useWalletWithPositionsQuery } from 'api/queries/walletPositionsQueries';
import {
  getBestPriceFromDisplayedOrderbook,
  getSpreadPercentageFromDisplayedOrderbook,
} from 'components/orders/orderBook/orderBookService';
import { buildMarketPair } from 'lib/display/formatting';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import { usePreFetchPersonalWalletSecret } from 'lib/ethereum/wallets';
import {
  useSelectMarketStepSize,
  useSelectMarketTickSize,
} from 'reduxState/markets/listed/listedMarketsUtils';
import { clearPrefilledOrderInputs } from 'reduxState/orders/prefillOrderInputs';
import { useSelector, staticDispatch } from 'reduxState/store';

import { reducer, defaultState } from './buyPanelReducer';
import { useBuySellCalculatedInfo } from './buySellInfo';
import BuySellPanel from './BuySellPanel';
import ConfirmOrderModal from './confirm/ConfirmOrderModal';
import { useFetchInitialMarginFractionOverride } from './leverage/leverageUtils';
import {
  checkQuantityUnderMinimum,
  isDustOrderAllowed,
} from './orderCalculations';
import { performOrder } from './orderExecute';

/** To prevent black background until Orderbook is ready */
const LoadingPlaceholder = styled.div`
  height: 100%;
  background: var(--elevation-light);
  border-radius: 2px;
`;

// TODO: refactor to bignumber library (bnjs.js light) and dont transform to number multiple times - keep both string * BN
function BuySellPanelWrap() {
  const { addOrUpdateOrderCachedQuery } = useOrdersQueriesInvalidator();
  const { data: walletPositionsData } = useWalletWithPositionsQuery();

  const { data: walletOrders } = useOrdersQuery();

  const currentMarket = useSelector(state => state.markets.currentMarket);
  const { data: marketData } = useMarketQuery(currentMarket.pair);

  const [preFilling, setPrefilling] = useState<boolean>();
  const wallet = useSelector(state => state.wallet);
  const skipBuySellConfirmation = useSelector(
    state => state.exchange.userGuide.skipBuySellConfirmation
  );
  const delegatedWallet = useSelector(
    state => state.delegatedKey.authorization
  );
  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );
  const priceDecimals = useSelectMarketTickSize(currentMarket.pair)
    ?.visibleDecimals;

  const quantityDecimals = useSelectMarketStepSize(currentMarket.pair)
    ?.visibleDecimals;

  const limitMakerOnlyMarket = marketData?.status
    ? marketData.status === MarketStatus.limitMakerOnly
    : undefined;

  const [state, dispatch] = useReducer(reducer, defaultState);

  const token1Base = currentMarket.base;
  const token2Quote = currentMarket.quote;

  // prefilledOrder = { type: buy/sell, price: '123', amountSum: '123' }
  const prefillOrderInputs = useSelector(
    state => state.orders.prefillOrderInputs
  );

  const initialMarginFractionOverride = useFetchInitialMarginFractionOverride({
    isVault: false,
    market: currentMarket.pair,
    walletAddress: wallet.walletAddress,
  });

  const buyingPower = calculateBuyingPower(
    walletPositionsData?.availableCollateral,
    marketData?.initialMarginFraction,
    initialMarginFractionOverride
  );

  const market = buildMarketPair(token1Base, token2Quote);
  const maximumPositionSize = market ? maximumPositionSizes[market] : undefined;

  useEffect(() => {
    if (limitMakerOnlyMarket === undefined) {
      return;
    }
    dispatch({
      type: 'marketChanged',
      limitMakerOnlyMarket,
    });
  }, [limitMakerOnlyMarket, currentMarket.pair]);

  useEffect(() => {
    // Reset after user see Success animation
    if (state.state === 'successBuy' || state.state === 'successSell') {
      setTimeout(() => {
        dispatch({ type: 'resetState' });
      }, 900);
    }
  }, [state.state]);

  useEffect(() => {
    if (state.requiresReauthorize) {
      dispatch({ type: 'resetState' });
    }
    // Only run when delegated wallet changes (re-authorization happened)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegatedWallet.walletAddress]);

  useEffect(() => {
    // Safeguard conditions to prevent infinite loop
    if (
      preFilling ||
      !prefillOrderInputs.price ||
      buyingPower === undefined ||
      priceDecimals === undefined
    ) {
      return;
    }
    setPrefilling(true);

    // We are not switching sides on click
    // dispatch({ type: prefillOrderInputs.buyOrSell === 'buy' ? 'changeToBuy' : 'changeToSell', });

    dispatch({
      type: 'set',
      prop: 'price',
      value: prefillOrderInputs.price,
      priceDecimals,
      buyingPower,
    });
    staticDispatch(clearPrefilledOrderInputs());

    setPrefilling(false);
  }, [
    buyingPower,
    prefillOrderInputs.price,
    preFilling,
    state.orderType,
    priceDecimals,
    walletPositionsData?.availableCollateral,
    marketData?.initialMarginFraction,
  ]);

  // Prefetch Wallet Secret early to speed up first order
  usePreFetchPersonalWalletSecret(`${state.size}_${state.sizePercentage}`);

  useEffect(() => {
    // Safeguard conditions to prevent infinite loop
    if (
      preFilling ||
      !prefillOrderInputs.quantity ||
      quantityDecimals === undefined
    ) {
      return;
    }
    setPrefilling(true);

    dispatch({ type: 'set', prop: 'sizeInQuote', value: false });
    dispatch({
      type: 'set',
      prop: 'size',
      value: prefillOrderInputs.quantity,
      quantityDecimals,
    });
    staticDispatch(clearPrefilledOrderInputs());

    setPrefilling(false);
  }, [
    preFilling,
    priceDecimals,
    prefillOrderInputs.quantity,
    quantityDecimals,
  ]);

  const openPosition = walletPositionsData?.positions?.find(
    position => position.market === currentMarket.pair
  );

  const calculatedInfo = useBuySellCalculatedInfo({
    state,
    token1Base,
    token2Quote,
    buyingPower,
    walletOrders,
    openPosition: walletPositionsData?.positions
      ? openPosition || false
      : undefined,
    initialMarginFractionOverride,
  });

  const hasDustPosition =
    !!openPosition &&
    !!marketData &&
    Math.abs(Number(openPosition.quantity)) <
      Number(marketData.takerOrderMinimum);

  const quantityBelowMinimum = marketData
    ? checkQuantityUnderMinimum(state, marketData, hasDustPosition)
    : undefined;

  // Everything filled and non-zero
  const buyEnabled =
    !calculatedInfo?.revalidating &&
    !quantityBelowMinimum &&
    !!(state.orderType === OrderType.limit
      ? Number(state.price) && Number(calculatedInfo.quantityBuy)
      : Number(calculatedInfo.quantityBuy));

  const sellEnabled =
    !calculatedInfo?.revalidating &&
    !quantityBelowMinimum &&
    !!(state.orderType === OrderType.limit
      ? Number(state.price) && Number(calculatedInfo.quantitySell)
      : Number(calculatedInfo.quantitySell));

  const getOrderQuantity = (side: OrderSide) => {
    const quantity =
      side === 'buy' ? calculatedInfo.quantityBuy : calculatedInfo.quantitySell;
    if (isDustOrderAllowed(state, hasDustPosition) && quantity && marketData) {
      return `${Math.max(
        Number(quantity),
        Number(marketData.takerOrderMinimum)
      )}`;
    }
    return quantity;
  };

  const handlePerformOrder = (
    side: OrderSide,
    preConfirmCheck: boolean
  ): Promise<void> =>
    performOrder({
      quantity: getOrderQuantity(side),
      dispatch,
      state,
      side,
      token1Base,
      token2Quote,
      wallet,
      delegatedWallet,
      preConfirmCheck,
      stepSizeDecimals: quantityDecimals,
      getBestBidAskPrices: () => {
        const bestAsk = getBestPriceFromDisplayedOrderbook('ask');
        const bestBid = getBestPriceFromDisplayedOrderbook('bid');
        return { bestAsk, bestBid };
      },
      indexPrice: marketData?.indexPrice,
      conditionalTpSl: state.conditionalTpSl,
      conditionalTpSlMaximumPositionSize: maximumPositionSize,
    })
      .then(newOrder => {
        console.log('[handlePerformOrder] New', newOrder);
        if (newOrder) {
          // Update UI immediatelly (do not wait for WebSocket)
          addOrUpdateOrderCachedQuery(newOrder, false);
        }
        dispatch({ type: 'setConditionalTpSl', tpsl: undefined });
      })
      .catch(error => {
        console.warn(error);
        const isDelegatedKeyInvalid =
          error instanceof KatanaApiError &&
          error.idexErrorCode === 'DELEGATED_KEY_INVALID';
        dispatch({
          type: 'error',
          message: makeEthersErrorHumanRedable(error),
          // isDelegatedKeyInvalid
          //   ? 'Session expired. Re-authorize using the notification.'
          //   : makeEthersErrorHumanRedable(error),
          requiresReauthorize: isDelegatedKeyInvalid,
        });
      });

  const confirmBuyOrSell =
    state.state === 'confirmBuy' || state.state === 'confirmSell';

  return token1Base &&
    token2Quote &&
    priceDecimals !== undefined &&
    quantityDecimals !== undefined ? (
    <>
      {confirmBuyOrSell && (
        <ConfirmOrderModal
          onClose={() => dispatch({ type: 'closeConfirm' })}
          isBuy={state.state === 'confirmBuy'}
          onConfirm={() =>
            handlePerformOrder(
              state.state === 'confirmBuy' ? 'buy' : 'sell',
              false
            )
          }
        />
      )}
      <BuySellPanel
        state={state}
        dispatch={dispatch}
        performOrder={side =>
          handlePerformOrder(side, !skipBuySellConfirmation)
        }
        token1base={token1Base}
        token2quote={token2Quote}
        getBestPrice={() =>
          // TODO: BuySell - getBestPrice: replace state.buy ? 'bid' : 'ask' by index? price
          getBestPriceFromDisplayedOrderbook('bid')
        }
        getSpreadPercentage={() => getSpreadPercentageFromDisplayedOrderbook()}
        // feePercentage={feeRate ? Number(feeRate) * 100 : undefined}
        // fee={fee.value}
        // feeSymbol={fee.symbol || ''}
        buyEnabled={buyEnabled}
        sellEnabled={sellEnabled}
        priceDecimals={priceDecimals}
        quantityBelowMinimum={quantityBelowMinimum}
        hasDustPosition={hasDustPosition}
        quantityDecimals={quantityDecimals}
        marketData={marketData}
        calculatedInfo={calculatedInfo}
        buyingPower={buyingPower}
        initialMarginFractionOverride={initialMarginFractionOverride}
        walletMakerFee={walletPositionsData?.makerFeeRate}
        walletTakerFee={walletPositionsData?.takerFeeRate}
      />
    </>
  ) : (
    <LoadingPlaceholder />
  );
}

export default BuySellPanelWrap;
